import React, { useState } from 'react';
import { toast } from 'react-toastify';
import usePendingTomPayments from '../hooks/usePendingTomPayments';
import { formatAmount, submitPendingTomPayment } from '../utils/tomPayments';
import CommonModal from './modals/CommonModal';
import '../styles/offline.css';

const when = (ts) => {
  try {
    return new Date(ts).toLocaleString();
  } catch (e) {
    return '';
  }
};

/**
 * Persistent banner (same style as the offline banner, rendered next to it in the header) while
 * card payments approved on the terminal are still not recorded on the server - staff closed the
 * dialog, or the app restarted. [Review] lists them with a Retry each.
 */
const TomPendingBanner = () => {
  const list = usePendingTomPayments();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  if (list.length === 0 && !open) return null;

  const retry = async (rec) => {
    setBusyId(rec.id);
    try {
      const r = await submitPendingTomPayment(rec.id, { manual: true });
      if (r.outcome === 'recorded') toast.success('Card payment recorded');
      else if (r.outcome === 'already_paid') toast.success('That order was already recorded');
      else if (r.outcome !== 'busy' && r.outcome !== 'missing') toast.error(r.message || 'Could not record the payment');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {list.length > 0 && (
        <div className="offlineBanner" role="alert" data-testid="tom-pending-banner">
          <div className="offlineBannerText">
            <strong>
              {list.length} card payment{list.length === 1 ? '' : 's'} approved but not recorded
            </strong>
            <span>The customer was charged on the terminal. Do not charge again; record it here.</span>
          </div>
          <button type="button" className="offlineBannerBtn" onClick={() => setOpen(true)}>
            Review
          </button>
        </div>
      )}
      <CommonModal isOpen={open} onClose={() => setOpen(false)} elId="tomPendingList">
        <div className="modal-header">
          <h5 className="modal-title">Card payments not recorded</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setOpen(false)} />
        </div>
        <div className="modal-body">
          {list.length === 0 ? (
            <p>Nothing left to record.</p>
          ) : (
            <ul className="list-unstyled mb-0">
              {list.map((rec) => (
                <li key={rec.id} className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom" data-testid="tom-pending-item">
                  <div>
                    <div>
                      <strong>{rec.orderNumber ? `Order ${rec.orderNumber}` : rec.orderId ? `Order #${rec.orderId}` : 'Order'}</strong>
                      {formatAmount(rec.amount) ? ` - ${formatAmount(rec.amount)}` : ''}
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.8 }}>Approved {when(rec.createdAt)}</div>
                    {rec.lastError && <div className="text-danger" style={{ fontSize: 13 }}>{rec.lastError}</div>}
                  </div>
                  <button type="button" className="btn btnPrimary" disabled={busyId === rec.id} onClick={() => retry(rec)}>
                    {busyId === rec.id ? 'Retrying…' : 'Retry'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>
            Close
          </button>
        </div>
      </CommonModal>
    </>
  );
};

export default TomPendingBanner;
